/**
 * System health query (Patch E28 / Patch 3).
 *
 * Polls /api/health on a low frequency to drive the workspace-level "MinIO
 * is down" banner. Polling: 60s when healthy, 15s when degraded — fast enough
 * that recovery shows up quickly, slow enough that the endpoint stays cheap.
 *
 * Renders only when storage.ok === false. The DB-down case isn't surfaced as
 * a banner because if the DB is gone, the app can't make API calls at all
 * (every request 500s) and the user already sees that.
 */

import { useQuery } from '@tanstack/react-query';
import { apiRequest } from './api-client';

export interface HealthResponse {
  status: 'ok';
  db: 'ok' | 'error';
  storage: { ok: boolean; reason?: string };
  uptime: number;
}

export const healthKey = ['system', 'health'] as const;

export function useSystemHealth() {
  return useQuery({
    queryKey: healthKey,
    queryFn: ({ signal }) =>
      apiRequest<HealthResponse>('/api/health', { signal }),
    refetchInterval: (q) =>
      q.state.data?.storage.ok === false ? 15_000 : 60_000,
    // We don't retry aggressively — if /api/health itself is failing,
    // the banner isn't helpful anyway.
    retry: 1,
    staleTime: 10_000,
  });
}

/**
 * Human-readable description of a storage failure reason. Falls back to a
 * generic message — operators check the API logs for the technical detail.
 */
export function describeStorageFailure(reason: string | undefined): string {
  switch (reason) {
    case 'bucket-missing':
      return 'storage bucket is misconfigured';
    case 'credentials':
      return 'storage credentials are invalid';
    case 'unreachable':
    case 'networkingerror':
    case 'unknownendpoint':
      return 'storage server is unreachable';
    default:
      return 'storage is unavailable';
  }
}

/**
 * Map a thrown upload error to user-friendly copy, with awareness of whether
 * the workspace banner is currently active (in which case we don't repeat
 * the explanation — we just point to it).
 */
export function describeUploadError(err: unknown, storageBannerActive: boolean): string {
  const base = (err as Error).message || 'Upload failed.';
  if (storageBannerActive) {
    return `${base} Document storage appears to be unavailable — see the banner above.`;
  }
  // Network-shaped errors → likely storage even without the banner
  const lower = base.toLowerCase();
  if (
    lower.includes('network') ||
    lower.includes('failed to fetch') ||
    lower.includes('cors') ||
    lower.includes('not found in storage')
  ) {
    return `${base} If this keeps happening, document storage may be unavailable — try again in a few moments.`;
  }
  return base;
}
